import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Template.css';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Template({ children, hideHeaderFooter = false }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored && stored !== 'undefined') {
        return JSON.parse(stored);
      }
      return null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('user');
      return null;
    }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.deepecon-nav-item')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        localStorage.removeItem('user');
        setUser(null);
        navigate('/login');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    navigate(path);
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const mainSiteUrl = process.env.NODE_ENV === 'production'
    ? 'https://deepecon.info'
    : 'http://localhost';

  return (
    <div className="deepecon-template">
      {!hideHeaderFooter && (
        <>
          {/* Universal Header - Matches main site */}
          <header className="deepecon-header">
            <div className="deepecon-header-container">
              {/* Logo */}
              <a href={mainSiteUrl} className="deepecon-logo">
                <img
                  src="/deepecon_network_logo.svg"
                  alt="DeepEcon"
                  className="deepecon-logo-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <span className="deepecon-tagline">
                  Where News Meets Deep Economic Intelligence
                </span>
              </a>

              {/* Desktop Navigation */}
              <nav className="deepecon-nav">
                {/* Free Content */}
                <div className="deepecon-nav-item">
                  <button
                    className="deepecon-nav-link"
                    onClick={() => toggleDropdown('free')}
                  >
                    Free ▾
                  </button>
                  {activeDropdown === 'free' && (
                    <div className="deepecon-dropdown-menu">
                      <a href={`${mainSiteUrl}/latest.html`} className="deepecon-dropdown-item">
                        Latest Analysis
                      </a>
                      <a href={`${mainSiteUrl}/united_states.html`} className="deepecon-dropdown-item">
                        US Focus
                      </a>
                      <a href={`${mainSiteUrl}/Editor_picks.html`} className="deepecon-dropdown-item">
                        Editor Picks
                      </a>
                      <a href={`${mainSiteUrl}/archive.html`} className="deepecon-dropdown-item">
                        Archive
                      </a>
                    </div>
                  )}
                </div>

                {/* Signals */}
                <div className="deepecon-nav-item">
                  <button
                    className="deepecon-nav-link"
                    onClick={() => toggleDropdown('signals')}
                  >
                    Signals ▾
                  </button>
                  {activeDropdown === 'signals' && (
                    <div className="deepecon-dropdown-menu">
                      <a href={`${mainSiteUrl}/sentiment_tracker/`} className="deepecon-dropdown-item">
                        Sentiment Tracker
                      </a>
                      <a href={`${mainSiteUrl}/sentiment_tracker/#breakpoints`} className="deepecon-dropdown-item">
                        Breakpoints
                      </a>
                      <div className="deepecon-dropdown-item-submenu">
                        <span>GDP Indices ›</span>
                        <div className="deepecon-submenu">
                          <a href={`${mainSiteUrl}/sentiment_tracker/#indices/consumption`}>Consumption</a>
                          <a href={`${mainSiteUrl}/sentiment_tracker/#indices/investment`}>Investment</a>
                          <a href={`${mainSiteUrl}/sentiment_tracker/#indices/fiscal`}>Fiscal Policy</a>
                          <a href={`${mainSiteUrl}/sentiment_tracker/#indices/trade`}>Trade</a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Commentary */}
                <div className="deepecon-nav-item">
                  <button
                    className="deepecon-nav-link"
                    onClick={() => toggleDropdown('commentary')}
                  >
                    Commentary ▾
                  </button>
                  {activeDropdown === 'commentary' && (
                    <div className="deepecon-dropdown-menu">
                      <a href={`${mainSiteUrl}/economic_desk/`} className="deepecon-dropdown-item">
                        Economic Desk
                      </a>
                      <a href={`${mainSiteUrl}/Editor_picks.html`} className="deepecon-dropdown-item">
                        Editorial
                      </a>
                      <a href={`${mainSiteUrl}/archive.html`} className="deepecon-dropdown-item">
                        Macro Analysis
                      </a>
                      <a href={`${mainSiteUrl}/united_states.html`} className="deepecon-dropdown-item">
                        Regional
                      </a>
                    </div>
                  )}
                </div>

                {/* Methodology */}
                <div className="deepecon-nav-item">
                  <button
                    className="deepecon-nav-link"
                    onClick={() => toggleDropdown('methodology')}
                  >
                    Methodology ▾
                  </button>
                  {activeDropdown === 'methodology' && (
                    <div className="deepecon-dropdown-menu">
                      <a href={`${mainSiteUrl}/how_it_works.html`} className="deepecon-dropdown-item">
                        How It Works
                      </a>
                      <a href={`${mainSiteUrl}/models.html`} className="deepecon-dropdown-item">
                        Models
                      </a>
                      <a href={`${mainSiteUrl}/data_sources.html`} className="deepecon-dropdown-item">
                        Data Sources
                      </a>
                      <a href={`${mainSiteUrl}/calibration.html`} className="deepecon-dropdown-item">
                        Calibration
                      </a>
                      <a href={`${mainSiteUrl}/limitations.html`} className="deepecon-dropdown-item">
                        Limitations
                      </a>
                    </div>
                  )}
                </div>

                {/* Account Menu */}
                <div className="deepecon-nav-item">
                  <button
                    className="deepecon-nav-link deepecon-nav-link-account"
                    onClick={() => toggleDropdown('account')}
                  >
                    {user ? `${user.username} ▾` : 'Account ▾'}
                  </button>
                  {activeDropdown === 'account' && (
                    <div className="deepecon-dropdown-menu deepecon-dropdown-menu-right">
                      {user ? (
                        <>
                          <button
                            onClick={() => handleNavigation('/dashboard')}
                            className="deepecon-dropdown-item"
                          >
                            Dashboard
                          </button>
                          <button
                            onClick={() => handleNavigation('/settings')}
                            className="deepecon-dropdown-item"
                          >
                            Settings
                          </button>
                          <button
                            onClick={() => handleNavigation('/api-keys')}
                            className="deepecon-dropdown-item"
                          >
                            API Keys
                          </button>
                          <div className="deepecon-dropdown-divider"></div>
                          <button
                            onClick={handleLogout}
                            className="deepecon-dropdown-item"
                          >
                            Logout
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleNavigation('/login')}
                            className="deepecon-dropdown-item"
                          >
                            Login
                          </button>
                          <button
                            onClick={() => handleNavigation('/register')}
                            className="deepecon-dropdown-item"
                          >
                            Register
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </nav>

              {/* Mobile Menu Toggle */}
              <button
                className="deepecon-mobile-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className="deepecon-mobile-menu">
                <div className="deepecon-mobile-menu-section">
                  <div className="deepecon-mobile-menu-title">Free</div>
                  <a href={`${mainSiteUrl}/latest.html`}>Latest Analysis</a>
                  <a href={`${mainSiteUrl}/united_states.html`}>US Focus</a>
                  <a href={`${mainSiteUrl}/Editor_picks.html`}>Editor Picks</a>
                  <a href={`${mainSiteUrl}/archive.html`}>Archive</a>
                </div>

                <div className="deepecon-mobile-menu-section">
                  <div className="deepecon-mobile-menu-title">Signals</div>
                  <a href={`${mainSiteUrl}/sentiment_tracker/`}>Sentiment Tracker</a>
                  <a href={`${mainSiteUrl}/sentiment_tracker/#breakpoints`}>Breakpoints</a>
                </div>

                <div className="deepecon-mobile-menu-section">
                  <div className="deepecon-mobile-menu-title">Commentary</div>
                  <a href={`${mainSiteUrl}/economic_desk/`}>Economic Desk</a>
                  <a href={`${mainSiteUrl}/Editor_picks.html`}>Editorial</a>
                </div>

                <div className="deepecon-mobile-menu-section">
                  <div className="deepecon-mobile-menu-title">Account</div>
                  {user ? (
                    <>
                      <button onClick={() => handleNavigation('/dashboard')}>Dashboard</button>
                      <button onClick={() => handleNavigation('/settings')}>Settings</button>
                      <button onClick={() => handleNavigation('/api-keys')}>API Keys</button>
                      <button onClick={handleLogout}>Logout</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleNavigation('/login')}>Login</button>
                      <button onClick={() => handleNavigation('/register')}>Register</button>
                    </>
                  )}
                </div>
              </div>
            )}
          </header>
        </>
      )}

      {/* Main Content */}
      <main className="deepecon-main">
        {children}
      </main>

      {!hideHeaderFooter && (
        <footer className="deepecon-footer">
          <div className="deepecon-footer-content">
            <div className="deepecon-footer-section">
              <h3>DeepEcon</h3>
              <p>AI-powered economic sentiment and GDP impact insights for everyone.</p>
            </div>
            <div className="deepecon-footer-section">
              <a href={`${mainSiteUrl}/archive.html`}>Open Access Archive</a>
              <a href="mailto:contact@deepecon.info">Contact</a>
              <a href="https://www.instagram.com/deepecon/" target="_blank" rel="noopener noreferrer">
                Instagram @DeepEcon
              </a>
              <p style={{ fontSize: '0.8rem', marginTop: '1rem', color: '#6b7280' }}>
                © 2026 DeepEcon · Privacy · Terms
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#999' }}>
            This is an automated economic impact analysis system. Analyses are generated from news content and do not constitute financial or investment advice.
          </div>
        </footer>
      )}
    </div>
  );
}

export default Template;
